import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart3,
  MessageSquare,
  TrendingUp,
  Building2,
  Cpu,
  FileText,
  Zap,
  Activity,
  Bot,
  Database,
  RotateCcw,
  RefreshCw,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Calculator,
  Search,
  CheckCircle2,
  HelpCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TENANT_ID, API_BASE_URL } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import AIMessageUsageTable from './AIMessageUsageTable';
import {
  formatCostUSD,
  formatTokenCount,
  type PricingCatalogResponse,
} from '../utils/aiPricing';
import '../styles/ai-usage.css';

interface UsageRecord {
  id: string;
  tenantId: string;
  date: string;
  provider: string;
  model: string;
  type: 'chat' | 'embedding' | 'system_one' | string;
  requestsUsed: number;
  tokensUsed: number;
}

interface TenantModelUsage {
  model: string;
  provider: string;
  requestsUsed: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCostUSD: number;
  isFreeTier: boolean;
}

interface TenantUsage {
  tenantId: string;
  tenantName: string | null;
  tenantSlug: string | null;
  requestsUsed: number;
  tokensUsed: number;
  promptTokens?: number;
  completionTokens?: number;
  estimatedCostUSD?: number;
  avgCostPerRequestUSD?: number;
  avgTokensPerRequest?: number;
  percentageOfTotalCost?: number;
  freeTierRequests?: number;
  paidRequests?: number;
  modelsUsed?: TenantModelUsage[];
}

interface ProviderUsage {
  provider: string;
  model: string;
  type: 'chat' | 'embedding' | 'system_one' | string;
  _sum: {
    requestsUsed: number;
    tokensUsed: number;
  };
  pricing?: {
    promptPerMillion: number;
    completionPerMillion: number;
    isFreeTier: boolean;
  };
  estimatedCostUSD?: number;
}

interface DailyTotal {
  date: string;
  _sum: {
    requestsUsed: number;
    tokensUsed: number;
  };
}

const PROVIDER_META: Record<string, { color: string; label: string }> = {
  embeddinggemma: { color: '#10b981', label: 'EmbeddingGemma' },
  jina: { color: '#0d9488', label: 'Jina' },
  mistral: { color: '#ea580c', label: 'Mistral' },
  gemini: { color: '#2563eb', label: 'Gemini' },
  openrouter: { color: '#7c3aed', label: 'OpenRouter' },
  groq: { color: '#d97706', label: 'Groq' },
  nvidia: { color: '#16a34a', label: 'Nvidia' },
  openai: { color: '#334155', label: 'OpenAI' },
  ollama: { color: '#9333ea', label: 'Ollama' },
  cerebras: { color: '#c026d3', label: 'Cerebras' },
  jev: { color: '#0284c7', label: 'Jev (System One)' },
  opencode: { color: '#6366f1', label: 'OpenCode Zen' },
  deepseek: { color: '#0ea5e9', label: 'DeepSeek' },
};

type TabId = 'messages' | 'overview' | 'tenants' | 'providers' | 'costs' | 'records';

const AIUsagePage: React.FC = () => {
  const { token } = useAuth();
  const { addToast } = useToast();
  const [records, setRecords] = useState<UsageRecord[]>([]);
  const [tenants, setTenants] = useState<TenantUsage[]>([]);
  const [providers, setProviders] = useState<ProviderUsage[]>([]);
  const [dailyTotals, setDailyTotals] = useState<DailyTotal[]>([]);
  const [pricingCatalogData, setPricingCatalogData] = useState<PricingCatalogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('messages');

  // Drill-down filters passed to AIMessageUsageTable
  const [drilldownTenantId, setDrilldownTenantId] = useState('');
  const [drilldownProvider, setDrilldownProvider] = useState('');

  // Simulator state in 'costs' tab
  const [simConversations, setSimConversations] = useState(1000);
  const [simMsgsPerConv, setSimMsgsPerConv] = useState(4);
  const [simPromptTokens, setSimPromptTokens] = useState(700);
  const [simCompletionTokens, setSimCompletionTokens] = useState(250);
  const [simSelectedModelId, setSimSelectedModelId] = useState('llama-3.3-70b-versatile');

  // Catalog search in 'costs' tab
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogProviderFilter, setCatalogProviderFilter] = useState('');

  // Aggregated records filters
  const [filters, setFilters] = useState({
    tenantId: '',
    startDate: '',
    endDate: '',
    provider: '',
    model: '',
    type: '' as 'chat' | 'embedding' | 'system_one' | '',
    page: 1,
    limit: 50,
  });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });

  const headers: HeadersInit = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
  };

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (filters.tenantId) params.append('tenantId', filters.tenantId);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.provider) params.append('provider', filters.provider);
    if (filters.model) params.append('model', filters.model);
    if (filters.type) params.append('type', filters.type);
    params.append('page', filters.page.toString());
    params.append('limit', filters.limit.toString());
    return params.toString();
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-usage?${buildQuery()}`, { headers });
      if (!res.ok) throw new Error('Failed to load usage records');
      const data = await res.json();
      setRecords(data.records || []);
      setPagination({
        total: data.pagination?.total ?? 0,
        totalPages: data.pagination?.totalPages ?? 0,
      });
      setDailyTotals(data.dailyTotals || []);
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'No se pudieron cargar los registros de uso' });
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-usage/tenants`, { headers });
      if (!res.ok) throw new Error('Failed to load tenant usage');
      const data = await res.json();
      setTenants(Array.isArray(data) ? data : []);
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'No se pudo cargar el resumen por tenant' });
    }
  };

  const loadProviders = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-usage/providers`, { headers });
      if (!res.ok) throw new Error('Failed to load provider usage');
      const data = await res.json();
      setProviders(Array.isArray(data) ? data : []);
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'No se pudo cargar el resumen por provider' });
    }
  };

  const loadPricingCatalog = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/ai-usage/pricing`, { headers });
      if (!res.ok) return;
      const data: PricingCatalogResponse = await res.json();
      setPricingCatalogData(data);
    } catch {
      // Non-blocking
    }
  };

  useEffect(() => {
    void loadRecords();
    void loadTenants();
    void loadProviders();
    void loadPricingCatalog();
  }, [filters.tenantId, filters.startDate, filters.endDate, filters.provider, filters.model, filters.type, filters.page]);

  const formatNumber = (num: number) => num.toLocaleString();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
  };

  const totalPlatformCostUSD = useMemo(() => {
    return tenants.reduce((acc, t) => acc + (t.estimatedCostUSD || 0), 0);
  }, [tenants]);

  const getTotalStats = () => {
    const chatTokens = providers.filter((p) => p.type === 'chat').reduce((sum, p) => sum + p._sum.tokensUsed, 0);
    const embeddingTokens = providers
      .filter((p) => p.type === 'embedding')
      .reduce((sum, p) => sum + p._sum.tokensUsed, 0);
    const systemOneTokens = providers
      .filter((p) => p.type === 'system_one')
      .reduce((sum, p) => sum + p._sum.tokensUsed, 0);
    const chatRequests = providers.filter((p) => p.type === 'chat').reduce((sum, p) => sum + p._sum.requestsUsed, 0);
    const embeddingRequests = providers
      .filter((p) => p.type === 'embedding')
      .reduce((sum, p) => sum + p._sum.requestsUsed, 0);
    const systemOneRequests = providers
      .filter((p) => p.type === 'system_one')
      .reduce((sum, p) => sum + p._sum.requestsUsed, 0);

    return {
      totalTokens: chatTokens + embeddingTokens + systemOneTokens,
      chatTokens,
      embeddingTokens,
      systemOneTokens,
      totalRequests: chatRequests + embeddingRequests + systemOneRequests,
      chatRequests,
      embeddingRequests,
      systemOneRequests,
    };
  };

  const stats = getTotalStats();

  const clearFilters = () => {
    setFilters({
      tenantId: '',
      startDate: '',
      endDate: '',
      provider: '',
      model: '',
      type: '',
      page: 1,
      limit: 50,
    });
  };

  const handleDrilldownTenant = (tenantId: string) => {
    setDrilldownTenantId(tenantId);
    setDrilldownProvider('');
    setActiveTab('messages');
  };

  const handleDrilldownProvider = (provider: string) => {
    setDrilldownProvider(provider.toLowerCase());
    setDrilldownTenantId('');
    setActiveTab('messages');
  };

  const hasActiveFilters = Boolean(
    filters.tenantId || filters.startDate || filters.endDate || filters.provider || filters.model || filters.type
  );

  const getTenantName = (tenantId: string) => {
    const found = tenants.find((t) => t.tenantId === tenantId);
    return found?.tenantName || found?.tenantSlug || `${tenantId.substring(0, 8)}...`;
  };

  // Cost simulator calculations
  const simulatorResult = useMemo(() => {
    const catalog = pricingCatalogData?.catalog || [];
    const selectedModel = catalog.find((m) => m.modelId === simSelectedModelId);

    const isFree = selectedModel?.isFreeTier ?? (simSelectedModelId.includes(':free') || simSelectedModelId.includes('free') || simSelectedModelId.includes('jev'));
    const inputRate = selectedModel ? selectedModel.inputCostPerMillion : 0.59;
    const outputRate = selectedModel ? selectedModel.outputCostPerMillion : 0.79;

    const totalMessages = Math.max(0, simConversations) * Math.max(1, simMsgsPerConv);
    const totalPromptTokens = totalMessages * Math.max(0, simPromptTokens);
    const totalCompletionTokens = totalMessages * Math.max(0, simCompletionTokens);
    const totalTokens = totalPromptTokens + totalCompletionTokens;

    const promptCost = isFree ? 0 : (totalPromptTokens * inputRate) / 1_000_000;
    const completionCost = isFree ? 0 : (totalCompletionTokens * outputRate) / 1_000_000;
    const totalMonthlyCost = promptCost + completionCost;
    const costPerConversation = simConversations > 0 ? totalMonthlyCost / simConversations : 0;
    const costPerMessage = totalMessages > 0 ? totalMonthlyCost / totalMessages : 0;

    return {
      selectedModel,
      isFree,
      inputRate,
      outputRate,
      totalMessages,
      totalPromptTokens,
      totalCompletionTokens,
      totalTokens,
      promptCost,
      completionCost,
      totalMonthlyCost,
      costPerConversation,
      costPerMessage,
    };
  }, [
    pricingCatalogData,
    simSelectedModelId,
    simConversations,
    simMsgsPerConv,
    simPromptTokens,
    simCompletionTokens,
  ]);

  // Filtered catalog for catalog table
  const filteredCatalog = useMemo(() => {
    const list = pricingCatalogData?.catalog || [];
    return list.filter((item) => {
      if (catalogProviderFilter && item.provider.toLowerCase() !== catalogProviderFilter.toLowerCase()) {
        return false;
      }
      if (catalogSearch.trim()) {
        const q = catalogSearch.toLowerCase().trim();
        return (
          item.displayName.toLowerCase().includes(q) ||
          item.modelId.toLowerCase().includes(q) ||
          item.provider.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [pricingCatalogData, catalogSearch, catalogProviderFilter]);

  return (
    <div className="ai-usage-page">
      {/* Page Header */}
      <div className="ai-usage-header">
        <div className="ai-usage-title-group">
          <div className="ai-usage-title-icon">
            <BarChart3 size={22} />
          </div>
          <div>
            <h2>Uso y Telemetría de IA</h2>
            <p className="page-subtitle">
              Auditoría en tiempo real de modelos del AI Router, consumo de tokens, estructura de costos y distribución por tenant
            </p>
          </div>
        </div>

        <div className="ai-usage-global-pills">
          <div className="global-stat-pill" title="Costo histórico total estimado de llamadas de IA">
            <DollarSign size={14} className="kpi-icon-violet" />
            <span className="font-mono">Global: {formatCostUSD(totalPlatformCostUSD)} USD</span>
          </div>
          <div className="global-stat-pill" title="Consumo histórico acumulado (Chat + Embeddings)">
            <Zap size={14} className="kpi-icon-blue" />
            <span>{formatTokenCount(stats.totalTokens)} tokens</span>
          </div>
          <div className="global-stat-pill" title="Promedio de tokens por mensaje o request global">
            <Cpu size={14} className="kpi-icon-indigo" />
            <span>~{stats.totalRequests > 0 ? Math.round(stats.totalTokens / stats.totalRequests).toLocaleString() : 0} tok/msg</span>
          </div>
          <div className="global-stat-pill" title="Tenants con actividad de IA">
            <Building2 size={14} className="kpi-icon-emerald" />
            <span>{tenants.length} tenants</span>
          </div>
          <button
            type="button"
            className="btn-refresh-header"
            onClick={() => {
              void loadRecords();
              void loadTenants();
              void loadProviders();
              void loadPricingCatalog();
            }}
            title="Refrescar métricas globales"
          >
            <RefreshCw size={14} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="tabs-navigation" role="tablist" aria-label="Vistas de uso de IA">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'messages'}
          className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          <MessageSquare size={16} />
          <span>Mensajes LLM</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'overview'}
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <TrendingUp size={16} />
          <span>Resumen Global</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'tenants'}
          className={`tab-button ${activeTab === 'tenants' ? 'active' : ''}`}
          onClick={() => setActiveTab('tenants')}
        >
          <Building2 size={16} />
          <span>Consumo por Tenant</span>
          <span className="tab-counter">{tenants.length}</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'providers'}
          className={`tab-button ${activeTab === 'providers' ? 'active' : ''}`}
          onClick={() => setActiveTab('providers')}
        >
          <Cpu size={16} />
          <span>Por Provider</span>
          <span className="tab-counter">{providers.length}</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'costs'}
          className={`tab-button ${activeTab === 'costs' ? 'active' : ''}`}
          onClick={() => setActiveTab('costs')}
        >
          <Calculator size={16} />
          <span>Estructura de Costos</span>
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'records'}
          className={`tab-button ${activeTab === 'records' ? 'active' : ''}`}
          onClick={() => setActiveTab('records')}
        >
          <FileText size={16} />
          <span>Consolidado Diario</span>
          <span className="tab-counter">{pagination.total}</span>
        </button>
      </div>

      {/* Global KPI Cards (Shown on aggregated tabs to avoid duplicating AIMessageUsageTable KPIs) */}
      {activeTab !== 'messages' && (
        <div className="summary-cards">
          <div className="summary-card violet-card">
            <div className="card-icon icon-violet">
              <DollarSign size={22} />
            </div>
            <div className="card-content">
              <h3>Costo Total Estimado</h3>
              <p className="card-value font-mono">{formatCostUSD(totalPlatformCostUSD)}</p>
              <p className="card-detail">Gasto acumulado de todos los tenants</p>
            </div>
          </div>

          <div className="summary-card primary">
            <div className="card-icon icon-blue">
              <Zap size={22} />
            </div>
            <div className="card-content">
              <h3>Total Tokens</h3>
              <p className="card-value">{formatTokenCount(stats.totalTokens)}</p>
              <p className="card-detail">
                {formatTokenCount(stats.chatTokens)} chat + {formatTokenCount(stats.embeddingTokens)} embed
                {stats.systemOneTokens > 0 ? ` + ${formatTokenCount(stats.systemOneTokens)} system one` : ''}
              </p>
            </div>
          </div>

          <div className="summary-card success">
            <div className="card-icon icon-emerald">
              <Activity size={22} />
            </div>
            <div className="card-content">
              <h3>Total Requests</h3>
              <p className="card-value">{formatNumber(stats.totalRequests)}</p>
              <p className="card-detail">
                {formatNumber(stats.chatRequests)} chat + {formatNumber(stats.embeddingRequests)} embed
              </p>
            </div>
          </div>

          <div className="summary-card info">
            <div className="card-icon icon-indigo">
              <Cpu size={22} />
            </div>
            <div className="card-content">
              <h3>Promedio / Mensaje</h3>
              <p className="card-value">
                ~{stats.totalRequests > 0 ? Math.round(stats.totalTokens / stats.totalRequests).toLocaleString() : 0} tok
              </p>
              <p className="card-detail font-mono">
                ~{formatCostUSD(stats.totalRequests > 0 ? totalPlatformCostUSD / stats.totalRequests : 0)} / request global
              </p>
            </div>
          </div>

          <div className="summary-card info">
            <div className="card-icon icon-indigo">
              <Building2 size={22} />
            </div>
            <div className="card-content">
              <h3>Tenants Activos</h3>
              <p className="card-value">{tenants.length}</p>
              <p className="card-detail">Con llamadas de IA procesadas</p>
            </div>
          </div>
        </div>
      )}

      {/* Contextual Filter Bar for Daily Aggregated Records & Overview */}
      {(activeTab === 'records' || activeTab === 'overview') && (
        <div className="filters-bar">
          <div className="filters-group">
            <div className="filter-item">
              <label htmlFor="agg-start-date">Fecha inicio</label>
              <input
                id="agg-start-date"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
              />
            </div>
            <div className="filter-item">
              <label htmlFor="agg-end-date">Fecha fin</label>
              <input
                id="agg-end-date"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
              />
            </div>
            <div className="filter-item">
              <label htmlFor="agg-tenant">Tenant</label>
              <select
                id="agg-tenant"
                value={filters.tenantId}
                onChange={(e) => setFilters({ ...filters, tenantId: e.target.value, page: 1 })}
              >
                <option value="">Todos los Tenants</option>
                {tenants.map((t) => (
                  <option key={t.tenantId} value={t.tenantId}>
                    {t.tenantName || t.tenantSlug || t.tenantId.substring(0, 10)}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="agg-type">Tipo</label>
              <select
                id="agg-type"
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as any, page: 1 })}
              >
                <option value="">Todos</option>
                <option value="chat">Chat</option>
                <option value="embedding">Embeddings</option>
                <option value="system_one">System One (Jev)</option>
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="agg-provider">Provider</label>
              <select
                id="agg-provider"
                value={filters.provider}
                onChange={(e) => setFilters({ ...filters, provider: e.target.value, page: 1 })}
              >
                <option value="">Todos</option>
                <option value="groq">Groq</option>
                <option value="openrouter">OpenRouter</option>
                <option value="mistral">Mistral</option>
                <option value="gemini">Gemini</option>
                <option value="nvidia">Nvidia</option>
                <option value="openai">OpenAI</option>
                <option value="deepseek">DeepSeek</option>
                <option value="jev">Jev</option>
                <option value="embeddinggemma">EmbeddingGemma</option>
              </select>
            </div>
          </div>
          {hasActiveFilters && (
            <button type="button" className="btn-reset-inline" onClick={clearFilters}>
              <RotateCcw size={14} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>
      )}

      {/* Tab Content */}
      <div className="tab-content">
        {/* Messages LLM Tab */}
        {activeTab === 'messages' && (
          <div className="messages-tab">
            <AIMessageUsageTable
              tenants={tenants}
              initialTenantId={drilldownTenantId}
              initialProvider={drilldownProvider}
            />
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="chart-card">
              <div className="chart-card-header">
                <TrendingUp size={18} className="kpi-icon-blue" />
                <h3>Tendencia de Consumo Diario (Últimos 7 días)</h3>
              </div>
              {dailyTotals.length > 0 ? (
                <div className="daily-chart">
                  {dailyTotals.slice(-7).map((d) => {
                    const maxTokens = Math.max(1, ...dailyTotals.slice(-7).map((x) => x._sum.tokensUsed));
                    const percentage = Math.max(4, (d._sum.tokensUsed / maxTokens) * 100);
                    return (
                      <div key={d.date} className="chart-bar-item">
                        <div className="chart-label">{formatDate(d.date)}</div>
                        <div className="chart-bar-container">
                          <div
                            className="chart-bar"
                            style={{ width: `${percentage}%` }}
                            title={`${formatTokenCount(d._sum.tokensUsed)} tokens`}
                          >
                            <span className="bar-value">{formatTokenCount(d._sum.tokensUsed)}</span>
                          </div>
                        </div>
                        <div className="chart-requests">{formatNumber(d._sum.requestsUsed)} req</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state-small">
                  <Database size={28} className="empty-state-bot-icon" />
                  <p>No hay datos diarios para el rango seleccionado</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tenants Tab - Ranked by Financial Cost in USD */}
        {activeTab === 'tenants' && (
          <div className="tenants-tab">
            <div className="tenants-tab-intro">
              <div className="intro-text">
                <h3>Consumo Financiero de IA por Tenant</h3>
                <p>
                  Desglose exacto de costo en dólares ($ USD) calculado a partir de tokens de entrada (prompt) y tokens de salida (completion) multiplicados por la tarifa oficial de cada modelo.
                </p>
              </div>
              <div className="tenants-summary-badge">
                <span className="badge-label">Costo Total Tenants:</span>
                <span className="badge-amount font-mono">{formatCostUSD(totalPlatformCostUSD)} USD</span>
              </div>
            </div>

            {tenants.length > 0 ? (
              <div className="data-grid tenant-cards-grid">
                {tenants.map((t) => {
                  const cost = t.estimatedCostUSD ?? 0;
                  const pct = t.percentageOfTotalCost ?? (totalPlatformCostUSD > 0 ? Number(((cost / totalPlatformCostUSD) * 100).toFixed(1)) : 0);

                  return (
                    <div key={t.tenantId} className="tenant-card tenant-cost-card">
                      <div className="tenant-header">
                        <div className="tenant-icon">
                          <Building2 size={20} />
                        </div>
                        <div className="tenant-info">
                          <h4>{t.tenantName || t.tenantSlug || 'Sin nombre'}</h4>
                          <p className="tenant-id">{t.tenantId}</p>
                        </div>
                        <div className="tenant-cost-badge">
                          <span className="tenant-cost-val font-mono">{formatCostUSD(cost)}</span>
                          <span className="tenant-cost-pct font-mono">{pct}% del total</span>
                        </div>
                      </div>

                      {/* Cost percentage bar */}
                      <div className="cost-progress-track" title={`${pct}% del costo global de IA`}>
                        <div
                          className="cost-progress-fill"
                          style={{ width: `${Math.min(100, Math.max(3, pct))}%` }}
                        />
                      </div>

                      <div className="tenant-stats">
                        <div className="stat-item">
                          <span className="stat-label">Llamadas LLM</span>
                          <span className="stat-value">{formatNumber(t.requestsUsed)}</span>
                          <span className="stat-sub">
                            {t.paidRequests ?? 0} de pago • {t.freeTierRequests ?? 0} free
                          </span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Total Tokens</span>
                          <span className="stat-value">{formatTokenCount(t.tokensUsed)}</span>
                          <span className="stat-sub">
                            {formatTokenCount(t.promptTokens || 0)} in / {formatTokenCount(t.completionTokens || 0)} out
                          </span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Promedio / Mensaje</span>
                          <span className="stat-value">
                            ~{t.avgTokensPerRequest ?? (t.requestsUsed > 0 ? Math.round(t.tokensUsed / t.requestsUsed) : 0)} tok
                          </span>
                          <span className="stat-sub font-mono">
                            ~{formatCostUSD(t.avgCostPerRequestUSD ?? (t.requestsUsed > 0 ? cost / t.requestsUsed : 0))}/msg
                          </span>
                        </div>
                      </div>

                      {/* Top models used */}
                      {t.modelsUsed && t.modelsUsed.length > 0 && (
                        <div className="tenant-models-list">
                          <span className="models-list-title">Modelos Utilizados:</span>
                          <div className="models-pills-wrap">
                            {t.modelsUsed.map((m) => (
                              <span
                                key={`${m.provider}-${m.model}`}
                                className={`model-cost-tag ${m.isFreeTier ? 'tag-free' : 'tag-paid'}`}
                                title={`${m.requestsUsed} llamadas • ${formatTokenCount(m.totalTokens)} tokens • ${formatCostUSD(m.estimatedCostUSD)}`}
                              >
                                <code className="tag-model-name">{m.model}</code>
                                <span className="tag-model-cost font-mono">
                                  {m.isFreeTier ? 'Free' : formatCostUSD(m.estimatedCostUSD)}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="tenant-card-footer">
                        <button
                          type="button"
                          className="btn-drilldown"
                          onClick={() => handleDrilldownTenant(t.tenantId)}
                        >
                          <span>Auditar llamadas del tenant</span>
                          <ArrowUpRight size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <Building2 size={40} className="empty-state-bot-icon" />
                <h3>No hay tenants con uso registrado</h3>
                <p>Los datos financieros aparecerán tan pronto como los tenants interactúen con el bot</p>
              </div>
            )}
          </div>
        )}

        {/* Providers Tab */}
        {activeTab === 'providers' && (
          <div className="providers-tab">
            {providers.length > 0 ? (
              <div className="providers-list">
                {providers.map((p) => {
                  const key = p.provider.toLowerCase();
                  const meta = PROVIDER_META[key] || { color: '#475569', label: p.provider };
                  const isFree = p.pricing?.isFreeTier ?? (p.model.includes(':free') || p.provider === 'jev' || p.provider === 'ollama');

                  return (
                    <div key={`${p.provider}-${p.model}-${p.type}`} className="provider-item">
                      <div className="provider-header">
                        <div className="provider-icon-badge" style={{ background: `${meta.color}18`, color: meta.color }}>
                          <Cpu size={20} />
                        </div>
                        <div className="provider-details">
                          <h4>{meta.label}</h4>
                          <code className="model-code-badge">{p.model}</code>
                        </div>

                        <div className="provider-tags-group">
                          {isFree ? (
                            <span className="free-tier-pill" title="Modelo gratuito sin costo por token">
                              Free Tier ($0.00)
                            </span>
                          ) : p.pricing ? (
                            <span className="rate-badge font-mono" title="Tarifa oficial por millón de tokens">
                              In: ${p.pricing.promptPerMillion} / Out: ${p.pricing.completionPerMillion} /1M
                            </span>
                          ) : null}

                          <span className={`type-badge ${p.type}`}>
                            {p.type === 'chat' ? (
                              <>
                                <Bot size={13} />
                                <span>Chat</span>
                              </>
                            ) : p.type === 'system_one' ? (
                              <>
                                <Zap size={13} />
                                <span>System One</span>
                              </>
                            ) : (
                              <>
                                <Database size={13} />
                                <span>Embedding</span>
                              </>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="provider-metrics">
                        <div className="metric">
                          <span className="metric-value">{formatNumber(p._sum.requestsUsed)}</span>
                          <span className="metric-label">Llamadas</span>
                        </div>
                        <div className="metric">
                          <span className="metric-value">{formatTokenCount(p._sum.tokensUsed)}</span>
                          <span className="metric-label">Tokens Totales</span>
                        </div>
                        {p.estimatedCostUSD !== undefined && (
                          <div className="metric">
                            <span className="metric-value font-mono text-violet">
                              {isFree ? '$0.00' : formatCostUSD(p.estimatedCostUSD)}
                            </span>
                            <span className="metric-label">Costo Estimado</span>
                          </div>
                        )}
                        {(p.type === 'chat' || p.type === 'system_one') && (
                          <div className="metric metric-action">
                            <button
                              type="button"
                              className="btn-drilldown"
                              onClick={() => handleDrilldownProvider(p.provider)}
                            >
                              <span>Auditar llamadas</span>
                              <ArrowUpRight size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <Cpu size={40} className="empty-state-bot-icon" />
                <h3>No hay providers con uso registrado</h3>
                <p>Configura proveedores de IA para comenzar</p>
              </div>
            )}
          </div>
        )}

        {/* Costs Structure & Monthly Simulator Tab */}
        {activeTab === 'costs' && (
          <div className="costs-tab">
            {/* Cost Formula & Rules Card */}
            <div className="cost-rules-card">
              <div className="cost-rules-header">
                <Calculator size={20} className="kpi-icon-violet" />
                <div>
                  <h3>Fórmula Oficial y Estructura de Costos de IA</h3>
                  <p className="rules-subtitle">
                    Cómo se calculan los costos de los proveedores y modelos en la plataforma
                  </p>
                </div>
              </div>

              <div className="formula-box">
                <span className="formula-title">Fórmula Matemática Oficial:</span>
                <code className="formula-code">
                  Costo ($ USD) = (Tokens Prompt × Tarifa Entrada / 1,000,000) + (Tokens Completion × Tarifa Salida / 1,000,000)
                </code>
              </div>

              <div className="free-tier-notice-grid">
                <div className="notice-item">
                  <CheckCircle2 size={16} className="text-success" />
                  <div>
                    <strong>OpenRouter Free Tier (`:free`):</strong> Los modelos que terminan en <code>:free</code> (como <code>meta-llama/llama-3.3-70b-instruct:free</code>) o <code>openrouter/free</code> son 100% gratuitos ($0.00 USD) en tokens de entrada y salida.
                  </div>
                </div>

                <div className="notice-item">
                  <CheckCircle2 size={16} className="text-success" />
                  <div>
                    <strong>OpenRouter Auto (`openrouter/auto`):</strong> Router dinámico inteligente. Se computa con una tarifa ponderada de referencia de $0.40 in / $1.00 out por 1M de tokens.
                  </div>
                </div>

                <div className="notice-item">
                  <CheckCircle2 size={16} className="text-success" />
                  <div>
                    <strong>Jev / System One (`jev-1.13-free`):</strong> Clasificador rápido propio de TypeSafe AI para NLU y routing. Costo de tokens $0.00 USD (In-House Free Tier), optimizando el margen operativo.
                  </div>
                </div>

                <div className="notice-item">
                  <CheckCircle2 size={16} className="text-success" />
                  <div>
                    <strong>Groq, Cerebras & DeepSeek:</strong> Proveedores con altísima velocidad LPUs/TPUs y tarifas altamente competitivas (ej: Llama 3.3 70B a $0.59 in / $0.79 out por 1M).
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Monthly Cost Simulator */}
            <div className="simulator-section">
              <div className="simulator-card">
                <div className="simulator-header">
                  <div className="simulator-title-group">
                    <Activity size={18} className="kpi-icon-blue" />
                    <div>
                      <h3>Simulador de Costos Mensuales por Tenant</h3>
                      <p>Estima el costo mensual de IA según el volumen de clientes y el modelo seleccionado</p>
                    </div>
                  </div>
                </div>

                <div className="simulator-grid">
                  {/* Inputs Column */}
                  <div className="simulator-inputs">
                    <div className="sim-field">
                      <label htmlFor="sim-model">Modelo LLM Evaluado</label>
                      <select
                        id="sim-model"
                        value={simSelectedModelId}
                        onChange={(e) => setSimSelectedModelId(e.target.value)}
                      >
                        {pricingCatalogData?.catalog?.map((m) => (
                          <option key={m.modelId} value={m.modelId}>
                            {m.displayName} {m.isFreeTier ? '(Free Tier)' : `($${m.inputCostPerMillion}/$${m.outputCostPerMillion})`}
                          </option>
                        )) || (
                          <>
                            <option value="llama-3.3-70b-versatile">Groq · Llama 3.3 70B Versatile</option>
                            <option value="gpt-4o-mini">OpenAI · GPT-4o Mini</option>
                            <option value="gemini-2.5-flash">Gemini · 2.5 Flash</option>
                            <option value="openrouter/auto">OpenRouter · Auto Router</option>
                            <option value="meta-llama/llama-3.3-70b-instruct:free">OpenRouter · Llama 3.3 70B (Free)</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div className="sim-row-2">
                      <div className="sim-field">
                        <label htmlFor="sim-conv">Conversaciones / Mes</label>
                        <input
                          id="sim-conv"
                          type="number"
                          min="1"
                          max="1000000"
                          step="100"
                          value={simConversations}
                          onChange={(e) => setSimConversations(Number(e.target.value))}
                        />
                        <span className="field-hint">Clientes únicos atendidos al mes</span>
                      </div>

                      <div className="sim-field">
                        <label htmlFor="sim-msgs">Mensajes / Conversación</label>
                        <input
                          id="sim-msgs"
                          type="number"
                          min="1"
                          max="50"
                          value={simMsgsPerConv}
                          onChange={(e) => setSimMsgsPerConv(Number(e.target.value))}
                        />
                        <span className="field-hint">Promedio de turnos de chat</span>
                      </div>
                    </div>

                    <div className="sim-row-2">
                      <div className="sim-field">
                        <label htmlFor="sim-prompt-tok">Tokens Prompt (Entrada) / Msg</label>
                        <input
                          id="sim-prompt-tok"
                          type="number"
                          min="50"
                          max="8000"
                          step="50"
                          value={simPromptTokens}
                          onChange={(e) => setSimPromptTokens(Number(e.target.value))}
                        />
                        <span className="field-hint">Prompt del sistema + catálogo</span>
                      </div>

                      <div className="sim-field">
                        <label htmlFor="sim-comp-tok">Tokens Completion (Salida) / Msg</label>
                        <input
                          id="sim-comp-tok"
                          type="number"
                          min="20"
                          max="2000"
                          step="20"
                          value={simCompletionTokens}
                          onChange={(e) => setSimCompletionTokens(Number(e.target.value))}
                        />
                        <span className="field-hint">Respuesta generada por el bot</span>
                      </div>
                    </div>
                  </div>

                  {/* Outputs Display Column */}
                  <div className="simulator-results-panel">
                    <div className="sim-result-header">
                      <span>Proyección Mensual Estimada</span>
                      {simulatorResult.isFree ? (
                        <span className="free-tier-pill">100% Free Tier</span>
                      ) : (
                        <span className="badge-paid">Modelo Facturable</span>
                      )}
                    </div>

                    <div className="sim-big-number">
                      <span className="sim-amount font-mono">
                        {formatCostUSD(simulatorResult.totalMonthlyCost)}
                      </span>
                      <span className="sim-period">/ mes</span>
                    </div>

                    <div className="sim-breakdown-metrics">
                      <div className="metric-row">
                        <span>Total de Mensajes Bot:</span>
                        <strong>{simulatorResult.totalMessages.toLocaleString()} msgs</strong>
                      </div>
                      <div className="metric-row">
                        <span>Tokens Totales Mensuales:</span>
                        <strong>{formatTokenCount(simulatorResult.totalTokens)}</strong>
                      </div>
                      <div className="metric-row">
                        <span>Costo por Conversación:</span>
                        <strong className="font-mono text-violet">
                          {formatCostUSD(simulatorResult.costPerConversation)} USD
                        </strong>
                      </div>
                      <div className="metric-row">
                        <span>Costo por Mensaje Individual:</span>
                        <strong className="font-mono">
                          {formatCostUSD(simulatorResult.costPerMessage)} USD
                        </strong>
                      </div>
                      <div className="metric-row">
                        <span>Tarifas aplicadas (/ 1M tokens):</span>
                        <small className="font-mono text-muted">
                          Entrada: ${simulatorResult.inputRate} • Salida: ${simulatorResult.outputRate}
                        </small>
                      </div>
                    </div>

                    <div className="sim-takeaway">
                      <HelpCircle size={14} />
                      <span>
                        {simulatorResult.isFree
                          ? 'Este modelo no genera costo en dólares de API. Excelente para tiers freemium o pruebas piloto.'
                          : `Con ${simConversations.toLocaleString()} clientes mensuales, el costo de IA representa solo ${formatCostUSD(simulatorResult.costPerConversation)} por cada venta o atención.`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Official Pricing Catalog Table */}
            <div className="pricing-catalog-section">
              <div className="pricing-catalog-header">
                <div>
                  <h3>Catálogo Oficial de Tarifas de Modelos de IA</h3>
                  <p>Precios por millón (1M) de tokens y costo aproximado por llamada típica</p>
                </div>

                <div className="catalog-filters">
                  <div className="search-box">
                    <Search size={14} className="search-icon" />
                    <input
                      type="text"
                      placeholder="Buscar modelo o proveedor..."
                      value={catalogSearch}
                      onChange={(e) => setCatalogSearch(e.target.value)}
                    />
                  </div>

                  <select
                    value={catalogProviderFilter}
                    onChange={(e) => setCatalogProviderFilter(e.target.value)}
                  >
                    <option value="">Todos los Proveedores</option>
                    <option value="groq">Groq</option>
                    <option value="openrouter">OpenRouter</option>
                    <option value="openai">OpenAI</option>
                    <option value="gemini">Gemini</option>
                    <option value="deepseek">DeepSeek</option>
                    <option value="mistral">Mistral</option>
                    <option value="nvidia">Nvidia</option>
                    <option value="cerebras">Cerebras</option>
                    <option value="jev">Jev</option>
                    <option value="ollama">Ollama</option>
                  </select>
                </div>
              </div>

              <div className="records-table-scroll">
                <table className="modern-table pricing-catalog-table">
                  <thead>
                    <tr>
                      <th>Modelo y Proveedor</th>
                      <th>Categoría</th>
                      <th className="text-right">Tarifa Entrada (/ 1M)</th>
                      <th className="text-right">Tarifa Salida (/ 1M)</th>
                      <th className="text-right">Ej. 1K Prompt</th>
                      <th className="text-right">Ej. 500 Out</th>
                      <th className="text-center">Tipo de Cobro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCatalog.map((item) => (
                      <tr key={item.modelId}>
                        <td>
                          <div className="catalog-model-col">
                            <strong>{item.displayName}</strong>
                            <code className="model-code-badge">{item.modelId}</code>
                            {item.description && <span className="item-desc">{item.description}</span>}
                          </div>
                        </td>
                        <td>
                          <span className={`type-badge ${item.category}`}>
                            {item.category === 'chat'
                              ? 'Chat'
                              : item.category === 'system_one'
                              ? 'System One'
                              : item.category === 'embedding'
                              ? 'Embedding'
                              : item.category}
                          </span>
                        </td>
                        <td className="text-right font-mono">
                          {item.isFreeTier ? (
                            <span className="text-success">$0.00</span>
                          ) : (
                            `$${item.inputCostPerMillion.toFixed(2)}`
                          )}
                        </td>
                        <td className="text-right font-mono">
                          {item.isFreeTier ? (
                            <span className="text-success">$0.00</span>
                          ) : (
                            `$${item.outputCostPerMillion.toFixed(2)}`
                          )}
                        </td>
                        <td className="text-right font-mono text-muted">
                          {item.isFreeTier ? '$0.00' : formatCostUSD(item.examplePromptCost1K)}
                        </td>
                        <td className="text-right font-mono text-muted">
                          {item.isFreeTier ? '$0.00' : formatCostUSD(item.exampleCompletionCost500)}
                        </td>
                        <td className="text-center">
                          {item.isFreeTier ? (
                            <span className="free-tier-pill">Free Tier</span>
                          ) : (
                            <span className="badge-paid">Comercial</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Records Tab */}
        {activeTab === 'records' && (
          <div className="records-tab">
            {loading ? (
              <div className="skeleton-table-wrapper">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <div key={`agg-sk-${idx}`} className="skeleton-table-row">
                    <div className="skeleton-cell w-24" />
                    <div className="skeleton-cell w-32" />
                    <div className="skeleton-cell w-28" />
                    <div className="skeleton-cell w-40" />
                    <div className="skeleton-cell w-20" />
                    <div className="skeleton-cell w-20" />
                  </div>
                ))}
              </div>
            ) : records.length > 0 ? (
              <>
                <div className="records-table-scroll">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Tenant</th>
                        <th>Provider</th>
                        <th>Modelo</th>
                        <th>Tipo</th>
                        <th className="text-right">Requests</th>
                        <th className="text-right">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.map((r) => (
                        <tr key={r.id}>
                          <td>{r.date}</td>
                          <td>
                            <span className="tenant-badge-inline" title={r.tenantId}>
                              <Building2 size={13} className="tenant-inline-icon" />
                              {getTenantName(r.tenantId)}
                            </span>
                          </td>
                          <td>
                            <span className="provider-badge">
                              <Zap size={12} />
                              {r.provider}
                            </span>
                          </td>
                          <td>
                            <code className="model-code-badge">{r.model}</code>
                          </td>
                          <td>
                            <span className={`type-badge ${r.type}`}>
                              {r.type === 'chat' ? 'Chat' : r.type === 'system_one' ? 'System One' : 'Embedding'}
                            </span>
                          </td>
                          <td className="number-cell">{formatNumber(r.requestsUsed)}</td>
                          <td className="number-cell">{formatTokenCount(r.tokensUsed)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="telemetry-pagination-bar">
                    <span className="page-info">
                      Página {filters.page} de {pagination.totalPages} • {pagination.total} registros
                    </span>
                    <div className="pagination-controls">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={filters.page <= 1}
                        onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                      >
                        <ChevronLeft size={15} />
                        <span>Anterior</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={filters.page >= pagination.totalPages}
                        onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                      >
                        <span>Siguiente</span>
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <FileText size={40} className="empty-state-bot-icon" />
                <h3>No hay registros consolidados</h3>
                <p>Ajusta los filtros para ver más datos</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIUsagePage;